import { RefObject, memo } from "react";

import type { TMessage } from "@dreamcam/messenger-sdk";

import { Image } from "@components/Image";

import { useDialogStore } from "@storage/global/useDialogStore";
import { useMessengerStore } from "@storage/global/useMessengerStore";

import { DIALOG_KEYS } from "@services/DialogManager";

import { ICON_PATHS } from "@configs/icons";

import { convertTimeMessage, linkText } from "./helper";

const RenderSendingStatus = ({ read_at }: { read_at: string | null }) => (
	<>
		{read_at ? (
			<Image width={16} height={15} src={ICON_PATHS.READ} alt="read" />
		) : (
			<Image width={16} height={15} src={ICON_PATHS.SENT} alt="sent" />
		)}
	</>
);

interface MessageElementProps {
	messageValue: TMessage;
	isLastElement: RefObject<HTMLDivElement> | null;
}

export const MessageElement = memo(({ messageValue, isLastElement }: MessageElementProps) => {
	const { userMsgData } = useMessengerStore();
	const { openDialog, closeDialog } = useDialogStore();

	const isOwner = userMsgData.chat_client_id === messageValue.sender_id;

	const onOpenLinkDialog = (href: string) => {
		openDialog(DIALOG_KEYS.MESSAGE_LINK, {
			onFollowLink: () => {
				window.open(href, "_blank", "noopener noreferrer");
				closeDialog();
			},
		});
	};

	const parsedMessage = linkText(
		messageValue.content,
		userMsgData.chat_client_id !== messageValue.sender_id,
		onOpenLinkDialog,
	);

	const findAttachmentTip =
		messageValue.attachments && messageValue.attachments.find((attachment) => attachment.type === "tip");

	return (
		<div key={messageValue.id} id="message_elem" ref={isLastElement}>
			<div className={isOwner ? "flex justify-end" : "flex justify-start"}>
				<div
					className={
						findAttachmentTip
							? "mb-2 flex min-h-8.5 max-w-[70%] gap-2.5 rounded-[20px] border border-[#ffe457] bg-block"
							: isOwner
								? "mb-2 flex min-h-8.5 max-w-[70%] gap-2.5 rounded-[20px] bg-primary"
								: "mb-2 flex min-h-8.5 max-w-[70%] gap-2.5 rounded-[20px] bg-secondary"
					}
				>
					<div
						className={`relative max-w-full flex-1 text-sm font-normal wrap-break-word whitespace-break-spaces
							text-white`}
						id="Message-container"
						style={
							findAttachmentTip ? { display: "flex", alignItems: "center", margin: "5px 10px" } : { margin: "5px 13px" }
						}
					>
						{findAttachmentTip ? (
							<span className="mr-2.5">
								<Image width={16} height={16} src={ICON_PATHS.COIN} alt="coin" />
							</span>
						) : null}
						{findAttachmentTip ? `${isOwner ? "You sent" : "Sent"} ${findAttachmentTip.meta.amount} tk` : parsedMessage}

						<span
							className={`pointer-events-none invisible z-docked ms-4 inline-flex h-2.5 align-middle text-[10px]
								leading-none select-none [direction:ltr]`}
						>
							<span className="order-5 text-[17px] leading-2.5">
								{isOwner ? <RenderSendingStatus read_at={messageValue.read_at} /> : null}
							</span>
							<span
								className={`pointer-events-none invisible text-[10px] leading-none select-none [direction:ltr]
									[dir=auto]:[unicode-bidi:plaintext]`}
							>
								{convertTimeMessage(messageValue.sent_at)}
							</span>

							<div
								className={`p-inherit pointer-events-auto visible absolute inset-e-0 -bottom-px -me-0.5 flex h-2.5
									items-center gap-0.5 leading-none whitespace-nowrap`}
							>
								<span className="order-5 text-[17px] leading-2.5">
									{isOwner ? <RenderSendingStatus read_at={messageValue.read_at} /> : null}
								</span>

								<span
									className={
										isOwner
											? "flex items-end text-right text-xs leading-normal font-normal text-white select-none"
											: "flex items-end text-right text-xs leading-normal font-normal text-msg-time select-none"
									}
								>
									{convertTimeMessage(messageValue.sent_at)}
								</span>
							</div>
						</span>
					</div>
				</div>
			</div>
		</div>
	);
});
